"use client"
import ReactPlayer from "react-player/lazy"
import { Component } from "@/types"
import { useState } from "react"
import { Icon } from "../Icon"
import { cx } from "@/utils/cx"
import { tx } from "@/utils/tx"

export const COMPONENT_NAME = "videoPlayer"

export interface Props extends Component {
  url: null | string
  poster?: string
  autoplay?: boolean
  onLoad?: () => void
  onPlay?: () => void
  onPause?: () => void
  onRateChange?: (e: Event) => void
  onEnded?: () => void
  onStarted?: () => void
}

type VidePlayerStatus = "init" | "loading" | "playing" | "paused" | "error"

export function ReactVideoPlayer({
  url,
  autoplay = false,
  poster,
  className = ``,
  onLoad,
  onPlay,
  onPause,
  onRateChange,
  onStarted,
  onEnded,
  testId = COMPONENT_NAME,
}: Props) {
  const [status, setStatus] = useState<VidePlayerStatus>(autoplay ? "loading" : "init")
  const [hasBeenStarted, setHasBeenStarted] = useState(false)
  const [hasBeenWatched, setHasBeenWatched] = useState(false)

  const shouldShowThumbnail = autoplay === false && !!poster
  const shouldBeMutedOnStart = autoplay === true

  function handleEnded() {
    if (!hasBeenWatched) {
      setHasBeenWatched(true)
      typeof onEnded === `function` && onEnded()
    }
  }

  function handlePlay() {
    setStatus("playing")
    if (typeof onPlay === `function`) onPlay()
    if (!hasBeenStarted) {
      setHasBeenStarted(true)
      typeof onStarted === `function` && onStarted()
    }
  }

  function handleLoad() {
    if (shouldShowThumbnail) setStatus("playing")
    if (typeof onLoad === `function`) onLoad()
  }

  function handlePause() {
    setStatus("paused")
    if (typeof onPause === `function`) onPause()
  }

  function handleError() {
    setStatus("error")
  }

  return (
    <div
      className={cx(`rounded aspect-video w-full overflow-hidden`, className)}
      data-test={testId}
    >
      {!!url && (
        <ReactPlayer
          className=""
          url={url}
          controls
          muted={shouldBeMutedOnStart}
          light={shouldShowThumbnail ? poster : undefined}
          playing={status === "playing"}
          width="100%"
          height="100%"
          onReady={() => setStatus(autoplay ? "playing" : "init")}
          onLoadedData={handleLoad}
          onPlay={handlePlay}
          onPause={handlePause}
          onRateChange={onRateChange}
          onEnded={handleEnded}
          onError={handleError}
          data-test={tx(testId, "video")}
        />
      )}
      {!url && (
        <div className="aspect-video w-full bg-fill-secondary grid place-items-center"></div>
      )}
      {status === "error" && (
        <div className="aspect-video w-full bg-fill-secondary grid place-items-center">
          <div className="flex gap-1 items-center">
            <Icon name="playCircleFilledMd" className="w-8 h-8 text-text-subtle opacity-75" />
            <div className="text-text-subtle">Video not available</div>
          </div>
        </div>
      )}
    </div>
  )
}
